/**
 * Seite für den Pflanzen-Sitter (Urlaubsvertretung). Öffnet sich über einen
 * geheimen Link, braucht keine Anmeldung und zeigt nur, was zu tun ist.
 */
import { useEffect, useState } from 'react';
import { fmtDueRelative } from '@pflanzen/shared';
import { Button, Card, Pill } from '../ui/kit';

interface SitterTask {
  taskId: string;
  plant: string;
  emoji: string;
  room: string;
  action: string;
  doneLabel: string;
  icon: string;
  fingerTest: boolean;
  depthCm: number;
  state: string;
  daysUntilDue: number | null;
  handledToday: boolean;
  tips: string[];
}

export function SitterPage() {
  const secret = decodeURIComponent(location.hash.slice(1));
  const [data, setData] = useState<{ name: string; tasks: SitterTask[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});
  const load = () => {
    fetch(`/k/${encodeURIComponent(secret)}/sitter`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Dieser Link ist nicht (mehr) gültig.'))))
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };
  useEffect(load, [secret]);

  const mark = async (t: SitterTask, moist: boolean) => {
    const res = await fetch(`/k/${encodeURIComponent(secret)}/sitter/${encodeURIComponent(t.taskId)}${moist ? '?erde=feucht' : ''}`, { method: 'POST' });
    setDone((d) => ({ ...d, [t.taskId]: res.ok ? (moist ? 'noch feucht ✓' : `${t.doneLabel} ✓`) : 'Fehler' }));
  };

  if (error) return <p className="pt-safe px-5 pt-10 text-[15px]">{error}</p>;
  if (!data) return <p className="pt-safe px-5 pt-10 text-muted">Lädt …</p>;
  const open = data.tasks.filter((t) => !t.handledToday);

  return (
    <div className="paper pt-safe min-h-dvh px-4 pb-10">
      <h1 className="font-display mt-4 text-[32px] leading-tight">Danke fürs Aufpassen! 🌿</h1>
      <p className="mt-1 text-[15px] text-ink/75">Das brauchen die Pflanzen{data.name ? ` von ${data.name}` : ''}. Erst mit dem Finger prüfen, dann abhaken.</p>
      <div className="mt-5 space-y-3">
        {open.length === 0 && <Card>Heute ist nichts zu tun. 🙂</Card>}
        {open.map((t) => (
          <Card key={t.taskId} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[16px] font-semibold">
                {t.emoji} {t.plant} <span className="font-normal text-muted">· {t.room}</span>
              </div>
              <Pill tone={t.state === 'overdue' || t.state === 'due' ? 'water' : 'soil'}>{fmtDueRelative(t.daysUntilDue ?? 0)}</Pill>
            </div>
            <div className="text-[15px]">
              {t.icon} {t.action}
              {t.fingerTest && <span className="text-muted"> – Finger {t.depthCm} cm tief in die Erde: nur gießen, wenn sie trocken ist.</span>}
            </div>
            {t.tips.length > 0 && <p className="text-xs text-muted">💡 {t.tips[0]}</p>}
            {done[t.taskId] ? (
              <p className="rounded-2xl bg-moss-soft p-2.5 text-center text-sm font-semibold text-moss">{done[t.taskId]}</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button tone="water" onClick={() => void mark(t, false)}>
                  {t.doneLabel}
                </Button>
                {t.fingerTest ? (
                  <Button tone="ghost" onClick={() => void mark(t, true)}>
                    Noch feucht
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
